import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiUser, FiMic, FiDatabase, FiZap,
  FiPhone, FiSend, FiCheck, FiRefreshCw, FiTrash2, FiX, FiPlus,
} from 'react-icons/fi';
import type { VoiceAgent } from '../voice.types';
import {
  getAgentDetailDF,
  updateAgentDF,
  saveTransfersDF,
  getKbDocumentsDF,
  uploadKbDocumentDF,
  deleteKbDocumentDF,
  getToolsDF,
  createToolDF,
  assignToolsDF,
  getAgentCallActionDF,
  type DfKbDocumentRecord,
  type DfToolRecord,
} from '../../../services/voiceDataFabricService';
import ConfirmModal from './ConfirmModal';
import VoiceSelector from './VoiceSelector';
import '../../../styles/Voice.css';

/* ── Types ── */
interface Step {
  id: number;
  label: string;
  desc: string;
  icon: React.ReactNode;
}

interface AgentTool {
  elevenLabsToolId: string;
  name: string;
  description: string;
  apiMethod?: string | null;
}

interface TransferRule {
  id: number;
  phone: string;
  condition: string;
  type: string;
}

interface FnParam {
  id: number;
  dataType: string;
  identifier: string;
  description: string;
}

interface Props {
  agent: VoiceAgent;
  onBack: () => void;
}

/* ── Constants ── */
const STEPS: Step[] = [
  { id: 1, label: 'Agent Details',      desc: 'Set the name and purpose of your agent.',              icon: <FiUser size={14} /> },
  { id: 2, label: 'Voice Selection',    desc: 'Choose the voice personality.',                        icon: <FiMic size={14} /> },
  { id: 3, label: 'Knowledge Base',     desc: 'Provide documents for context.',                       icon: <FiDatabase size={14} /> },
  { id: 4, label: 'Functions',          desc: 'Integrate external tools for data or actions.',        icon: <FiZap size={14} /> },
  { id: 5, label: 'Transfer Directory', desc: 'Configure call transfer destinations.',                icon: <FiPhone size={14} /> },
  { id: 6, label: 'Call End Action',    desc: 'Send call details to a third-party API after the call ends.', icon: <FiSend size={14} /> },
];

const LLM_OPTIONS = [
  { value: 'gpt-4.1',           label: 'GPT 4.1 — ~$0.0517/min' },
  { value: 'gpt-4o',            label: 'GPT 4o — ~$0.0420/min' },
  { value: 'gpt-4o-mini',       label: 'GPT 4o Mini — ~$0.0180/min' },
  { value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet — ~$0.0600/min' },
];

/* ── Main component ── */
const AgentEditPanel: React.FC<Props> = ({ agent, onBack }) => {
  const [activeStep, setActiveStep] = useState(1);
  const [name,             setName]             = useState(agent.name);
  const [welcomeMessage,   setWelcomeMessage]   = useState(agent.welcomeMessage);
  const [systemMessage,    setSystemMessage]    = useState(agent.systemMessage);
  const [voicemailMessage, setVoicemailMessage] = useState(agent.voicemailMessage);
  const [llm,              setLlm]              = useState('gpt-4.1');
  const [detailLoading,    setDetailLoading]    = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<string>(agent.voiceId ?? '');
  const [allUserDocs,  setAllUserDocs]  = useState<DfKbDocumentRecord[]>([]);
  const [agentDocIds,  setAgentDocIds]  = useState<Set<string>>(new Set());
  const [docsLoading,  setDocsLoading]  = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [docToDelete,  setDocToDelete]  = useState<DfKbDocumentRecord | null>(null);
  const kbFileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchUserDocs = async () => {
    setDocsLoading(true);
    try {
      const docs = await getKbDocumentsDF();
      setAllUserDocs(docs);
    } catch { /* keep current */ }
    finally { setDocsLoading(false); }
  };

  const handleKbFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadKbDocumentDF(file);
      await fetchUserDocs();
      if (result.FileId) {
        setAgentDocIds((prev) => { const s = new Set(prev); s.add(result.FileId); return s; });
      }
      showToast(`Uploaded "${result.FileName || file.name}"`);
    } catch (err: any) {
      showToast(err.message ?? 'Upload failed', 'error');
    } finally { setUploading(false); }
  };

  const handleDeleteDoc = async () => {
    if (!docToDelete) return;
    try {
      await deleteKbDocumentDF(docToDelete.fileId);
      setAgentDocIds((prev) => { const s = new Set(prev); s.delete(docToDelete.fileId); return s; });
      await fetchUserDocs();
      showToast(`Deleted "${docToDelete.fileName}"`);
    } catch (err: any) {
      showToast(err.message ?? 'Delete failed', 'error');
    } finally { setDocToDelete(null); }
  };

  const fetchCompanyTools = async () => {
    setToolsLoading(true);
    try {
      const records = await getToolsDF();
      setAllTools(records.map((r) => ({
        elevenLabsToolId: r.elevenLabsToolId,
        name: r.toolName ?? '',
        description: r.toolDescription ?? '',
        apiMethod: r.apiMethod ?? null,
      })));
    } catch { /* keep current */ }
    finally { setToolsLoading(false); }
  };

  const toggleDocAssign = (fileId: string) => {
    setAgentDocIds((prev) => {
      const s = new Set(prev);
      s.has(fileId) ? s.delete(fileId) : s.add(fileId);
      return s;
    });
  };

  const [allTools,       setAllTools]       = useState<AgentTool[]>([]);
  const [assignedToolIds, setAssignedToolIds] = useState<Set<string>>(new Set());
  const [toolsLoading,   setToolsLoading]   = useState(false);
  const [creatingFn,     setCreatingFn]     = useState(false);
  const [transferRules, setTransferRules] = useState<TransferRule[]>([
    { id: 1, phone: '', condition: '', type: 'conference' },
  ]);
  const [nextRuleId, setNextRuleId] = useState(2);
  const [showCreateFn,  setShowCreateFn]  = useState(false);
  const [cfName,        setCfName]        = useState('');
  const [cfMethod,      setCfMethod]      = useState('GET');
  const [cfUrl,         setCfUrl]         = useState('');
  const [cfDesc,        setCfDesc]        = useState('');
  const [cfParams,      setCfParams]      = useState<FnParam[]>([
    { id: 1, dataType: 'String', identifier: '', description: '' },
  ]);
  const [cfNextParamId, setCfNextParamId] = useState(2);
  const [ceaUrl,         setCeaUrl]         = useState('');
  const [ceaMethod,      setCeaMethod]      = useState('POST');
  const [ceaContentType, setCeaContentType] = useState('application/json');
  const [ceaAuthType,    setCeaAuthType]    = useState('Basic');
  const [ceaAuthValue,   setCeaAuthValue]   = useState('');
  const [ceaAuthFocused, setCeaAuthFocused] = useState(false);
  const [ceaHasStoredAuth, setCeaHasStoredAuth] = useState(false);
  const [ceaAuthDirty,     setCeaAuthDirty]     = useState(false);
  const [ceaHeaders,     setCeaHeaders]     = useState('');
  const [ceaPayload,     setCeaPayload]     = useState('');
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* Fetch full agent detail + company KB docs on mount */
  useEffect(() => {
    if (!agent.elevenLabsAgentId) return;
    setDetailLoading(true);

    // Fetch company docs + tools + call-action in parallel
    fetchUserDocs();
    fetchCompanyTools();

    getAgentCallActionDF(agent.elevenLabsAgentId)
      .then((cea) => {
        if (cea.thirdPartyUrl)         setCeaUrl(cea.thirdPartyUrl);
        if (cea.thirdPartyMethod)      setCeaMethod(cea.thirdPartyMethod);
        if (cea.thirdPartyContentType) setCeaContentType(cea.thirdPartyContentType);
        if (cea.thirdPartyAuthType)    setCeaAuthType(cea.thirdPartyAuthType);
        if (cea.thirdPartyHeaders)     setCeaHeaders(cea.thirdPartyHeaders);
        if (cea.thirdPartyPayloadTemplate) setCeaPayload(cea.thirdPartyPayloadTemplate);
        if (cea.hasThirdPartyAuthValue) setCeaHasStoredAuth(true);
      })
      .catch(() => { /* no config yet — keep defaults */ });

    getAgentDetailDF(agent.elevenLabsAgentId)
      .then((d) => {
        const cfg      = d.conversation_config;
        const agentCfg = cfg?.agent;
        const prompt   = agentCfg?.prompt;

        if (agentCfg?.first_message) setWelcomeMessage(agentCfg.first_message);
        if (prompt?.prompt)          setSystemMessage(prompt.prompt);
        if (cfg?.tts?.voice_id)      setSelectedVoice(cfg.tts.voice_id);

        if (prompt?.llm) {
          const match = LLM_OPTIONS.find((o) => o.value === prompt!.llm || (prompt!.llm ?? '').includes(o.value));
          if (match) setLlm(match.value);
        }

        const allTools = prompt?.tools ?? [];

        // Voicemail message from built-in tool
        const vmTool = allTools.find((t) => t.params?.system_tool_type === 'voicemail_detection');
        if (vmTool?.params?.voicemail_message) setVoicemailMessage(vmTool.params.voicemail_message);

        // Knowledge Base — seed checked set from agent's assigned docs
        const kb = prompt?.knowledge_base ?? [];
        setAgentDocIds(new Set(kb.map((k) => k.id)));

        // Assigned tool IDs from the agent's ElevenLabs config
        const toolIds: string[] = (prompt as any)?.tool_ids ?? [];
        if (toolIds.length > 0) setAssignedToolIds(new Set(toolIds));

        // Transfer rules from built-in tool
        const transferTool = allTools.find((t) => t.params?.system_tool_type === 'transfer_to_number');
        const rawTransfers = transferTool?.params?.transfers ?? [];
        if (rawTransfers.length > 0) {
          setTransferRules(
            rawTransfers.map((tr, i) => ({
              id: i + 1,
              phone: tr.phone_number ?? '',
              condition: tr.condition ?? '',
              type: 'conference',
            })),
          );
        }
      })
      .catch(() => { /* fall back to list data */ })
      .finally(() => setDetailLoading(false));
  }, [agent.elevenLabsAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Handlers */

  const toggleToolAssign = (toolId: string) => {
    setAssignedToolIds((prev) => {
      const s = new Set(prev);
      s.has(toolId) ? s.delete(toolId) : s.add(toolId);
      return s;
    });
  };

  const addTransferRule = () => {
    setTransferRules((prev) => [
      ...prev,
      { id: nextRuleId, phone: '', condition: '', type: 'conference' },
    ]);
    setNextRuleId((n) => n + 1);
  };

  const removeTransferRule = (id: number) =>
    setTransferRules((prev) => prev.filter((r) => r.id !== id));

  const updateTransferRule = (id: number, field: keyof TransferRule, value: string) =>
    setTransferRules((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const addCfParam = () => {
    setCfParams((p) => [
      ...p,
      { id: cfNextParamId, dataType: 'String', identifier: '', description: '' },
    ]);
    setCfNextParamId((n) => n + 1);
  };

  const removeCfParam = (id: number) =>
    setCfParams((p) => p.filter((x) => x.id !== id));

  const updateCfParam = (id: number, field: keyof FnParam, value: string) =>
    setCfParams((p) => p.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const handleCreateFunction = async () => {
    if (!cfName.trim()) return;
    setCreatingFn(true);
    try {
      const result = await createToolDF({
        ToolName: cfName.trim(),
        ToolDescription: cfDesc.trim(),
        ApiUrl: cfUrl.trim(),
        ApiMethod: cfMethod,
        ToolParameters: cfParams
          .filter((p) => p.identifier.trim())
          .map((p) => ({
            Identifier: p.identifier.trim(),
            DataType: p.dataType.toLowerCase(),
            Description: p.description.trim(),
            IsRequired: true,
          })),
      });
      // Re-fetch all tools so the new one appears in the list
      await fetchCompanyTools();
      // Auto-assign the new tool to this agent
      if (result.elevenLabsToolId) {
        setAssignedToolIds((prev) => { const s = new Set(prev); s.add(result.elevenLabsToolId!); return s; });
      }
      setCfName(''); setCfMethod('GET'); setCfUrl(''); setCfDesc('');
      setCfParams([{ id: 1, dataType: 'String', identifier: '', description: '' }]);
      setCfNextParamId(2);
      setShowCreateFn(false);
      showToast('Function created successfully!');
    } catch (err: any) {
      showToast(err.message ?? 'Failed to create function', 'error');
    } finally { setCreatingFn(false); }
  };

  const handleSave = async () => {
    if (!agent.elevenLabsAgentId) return;
    setSaving(true);
    try {
      // Resolve auth value: empty string to clear, dirty value to set, null to keep stored
      let authValue: string | null;
      if (ceaAuthType === 'None') authValue = '';
      else if (ceaAuthDirty)      authValue = ceaAuthValue.trim();
      else                        authValue = null; // preserve existing stored secret

      // 1) Main agent update (details, KB, voice, call-end action)
      await updateAgentDF(agent.elevenLabsAgentId, {
        AgentName:                 name.trim(),
        WelcomeMessage:            welcomeMessage.trim(),
        SystemMessage:             systemMessage.trim(),
        VoiceSelection:            selectedVoice,
        VoiceMailMessage:          voicemailMessage.trim(),
        LlmModel:                  llm,
        PurchasedPhoneNumbers:     [],
        AddedNumbers:              [],
        KnowledgeBases:            allUserDocs
                                       .filter((doc) => agentDocIds.has(doc.fileId))
                                       .map((doc) => ({ type: 'file', name: doc.fileName, id: doc.fileId, usageMode: 'auto' })),
        ConversationConfig:        {},
        ThirdPartyUrl:             ceaUrl.trim() || null,
        ThirdPartyMethod:          ceaMethod,
        ThirdPartyContentType:     ceaContentType,
        ThirdPartyAuthType:        ceaAuthType,
        ThirdPartyAuthValue:       authValue,
        ThirdPartyHeaders:         ceaHeaders.trim() || null,
        ThirdPartyPayloadTemplate: ceaPayload.trim() || null,
      });

      // 2) Transfer rules (separate endpoint, same as Toolbox)
      const validTransfers = transferRules.filter((r) => r.phone.trim());
      if (validTransfers.length > 0) {
        await saveTransfersDF(
          agent.elevenLabsAgentId,
          validTransfers.map((r) => ({
            TransferPhoneNumber: r.phone.trim(),
            Condition: r.condition.trim(),
            TransferType: r.type,
          })),
        ).catch(() => { /* best-effort — main save already succeeded */ });
      }

      // 3) Tool assignments (separate endpoint, same as Toolbox)
      if (assignedToolIds.size > 0) {
        await assignToolsDF(
          agent.elevenLabsAgentId,
          Array.from(assignedToolIds),
        ).catch(() => { /* best-effort — main save already succeeded */ });
      }

      showToast('Agent updated successfully!');
      setTimeout(() => onBack(), 1200);
    } catch (err: any) {
      showToast(err.message ?? 'Save failed. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toastColor = toast?.type === 'success' ? '#10b981' : '#ef4444';

  return (
    <motion.div
      className='aep-root'
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.22 }}
    >
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: `${toastColor}18`,
          border: `1px solid ${toastColor}44`,
          borderRadius: 10, padding: '10px 16px',
          color: toastColor, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.type === 'success' ? <FiCheck size={14} /> : <FiX size={14} />}
          {toast.msg}
        </div>
      )}

      {/* Back button */}
      {/* <button className='aep-back-btn' onClick={onBack}>
        <FiArrowLeft size={14} />
        Back to Agents
      </button> */}

      {/* Stepper */}
      <div className='aep-stepper'>
        {STEPS.map((step, idx) => {
          const isActive    = step.id === activeStep;
          const isCompleted = step.id < activeStep;
          return (
            <React.Fragment key={step.id}>
              <div
                className={`aep-step${isActive ? ' aep-step--active' : ''}${isCompleted ? ' aep-step--done' : ''}`}
                onClick={() => setActiveStep(step.id)}
              >
                <div className='aep-step__circle'>
                  {isCompleted ? <FiCheck size={13} /> : step.id}
                </div>
                <div className='aep-step__meta'>
                  <span className='aep-step__label'>{step.label}</span>
                  <span className='aep-step__desc'>{step.desc}</span>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`aep-step__line${isCompleted ? ' aep-step__line--done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Panel body */}
      <div className='aep-body'>

        {/* ── Step 1: Agent Details ── */}
        {activeStep === 1 && (
          <motion.div
            key='step1'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            {detailLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Name of Agent */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className='skeleton skeleton-text' style={{ width: 120, height: 11 }} />
                  <div className='skeleton skeleton-rect' style={{ width: '100%', height: 40, borderRadius: 8 }} />
                </div>
                {/* Welcome Message */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className='skeleton skeleton-text' style={{ width: 150, height: 11 }} />
                  <div className='skeleton skeleton-rect' style={{ width: '100%', height: 40, borderRadius: 8 }} />
                </div>
                {/* System Message */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className='skeleton skeleton-text' style={{ width: 180, height: 11 }} />
                  <div className='skeleton skeleton-rect' style={{ width: '100%', height: 160, borderRadius: 8 }} />
                </div>
                {/* Voice Mail Message */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className='skeleton skeleton-text' style={{ width: 200, height: 11 }} />
                  <div className='skeleton skeleton-rect' style={{ width: '100%', height: 80, borderRadius: 8 }} />
                </div>
                {/* LLM */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className='skeleton skeleton-text' style={{ width: 140, height: 11 }} />
                  <div className='skeleton skeleton-rect' style={{ width: '100%', height: 40, borderRadius: 8 }} />
                </div>
              </div>
            )}

            {!detailLoading && (
              <>
                <div className='aep-field'>
                  <div className='aep-field__header'>
                    <label className='aep-label'>Name of Agent <span className='aep-required'>*</span></label>
                    <span className='aep-char-count'>{name.length}/20</span>
                  </div>
                  <input
                    className='aep-input'
                    value={name}
                    maxLength={20}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='e.g. Edge Agent'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>Welcome Message <span className='aep-required'>*</span></label>
                  <input
                    className='aep-input'
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    placeholder='Hi, how can I help you today?'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>System Message (Instructions) <span className='aep-required'>*</span></label>
                  <textarea
                    className='aep-textarea'
                    value={systemMessage}
                    rows={10}
                    onChange={(e) => setSystemMessage(e.target.value)}
                    placeholder='You are a helpful AI phone agent...'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>Voice Mail Message (Configuration)</label>
                  <textarea
                    className='aep-textarea'
                    value={voicemailMessage}
                    rows={4}
                    onChange={(e) => setVoicemailMessage(e.target.value)}
                    placeholder='Hi, this is an automated message. Please leave your details after the tone.'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>Language Model (LLM)</label>
                  <select className='aep-select' value={llm} onChange={(e) => setLlm(e.target.value)}>
                    {LLM_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <span className='aep-hint'>
                    Estimated price per minute is based on your system message length. Longer prompts cost more.
                  </span>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* ── Step 2: Voice Selection ── */}
        {activeStep === 2 && (
          <motion.div
            key='step2'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-voice-header'>
              <span className='aep-label'>Choose a voice for your agent</span>
            </div>

            <VoiceSelector selectedVoiceId={selectedVoice} onSelect={setSelectedVoice} />
          </motion.div>
        )}

        {/* ── Step 3: Knowledge Base ── */}
        {activeStep === 3 && (
          <motion.div
            key='step3'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-kb-header'>
              <div>
                <div className='aep-label aep-label--lg'>Knowledge Base Documents</div>
                <div className='aep-hint'>Select the documents this agent should use for context.</div>
              </div>
              <input
                ref={kbFileInputRef}
                type='file'
                accept='.pdf,.doc,.docx,.txt,.md'
                style={{ display: 'none' }}
                onChange={handleKbFileChange}
              />
              <button
                className='aep-kb-upload-btn'
                onClick={() => kbFileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <><FiRefreshCw size={12} className='pn-spin' /> Uploading…</> : '+ Upload New'}
              </button>
            </div>

            {(detailLoading || docsLoading) ? (
              <div className='aep-kb-list'>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className='aep-kb-row' style={{ gap: 10 }}>
                    <div className='skeleton skeleton-rect' style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }} />
                    <div className='skeleton skeleton-rect' style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0 }} />
                    <div className='skeleton skeleton-text' style={{ flex: 1, height: 11 }} />
                    <div className='skeleton skeleton-rect' style={{ width: 36, height: 18, borderRadius: 4 }} />
                  </div>
                ))}
              </div>
            ) : (
              <div className='aep-kb-list'>
                {allUserDocs.length === 0 ? (
                  <div className='aep-kb-empty-row'>
                    <FiDatabase size={14} />
                    <span>No documents uploaded yet.</span>
                  </div>
                ) : (
                  allUserDocs.map((doc) => {
                    const ext = doc.fileName?.split('.').pop()?.toUpperCase() ?? '';
                    return (
                      <div key={doc.fileId} className='aep-kb-row'>
                        <input
                          type='checkbox'
                          className='aep-kb-checkbox'
                          checked={agentDocIds.has(doc.fileId)}
                          onChange={() => toggleDocAssign(doc.fileId)}
                        />
                        <FiDatabase size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <span className='aep-kb-name'>{doc.fileName}</span>
                        {ext && <span className='aep-kb-type'>{ext}</span>}
                        <button
                          className='aep-kb-del-btn'
                          title='Delete document'
                          onClick={() => setDocToDelete(doc)}
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </motion.div>
        )}

        {docToDelete && (
          <ConfirmModal
            title='Delete Document'
            message={`Are you sure you want to permanently delete "${docToDelete.fileName}"? This will remove it from all agents.`}
            confirmLabel='Delete'
            onConfirm={handleDeleteDoc}
            onCancel={() => setDocToDelete(null)}
          />
        )}

        {/* ── Step 4: Functions ── */}
        {activeStep === 4 && (
          <motion.div
            key='step4'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-fn-header'>
              <div>
                <div className='aep-label aep-label--lg'>Available Functions (Tools)</div>
                <div className='aep-hint'>Select which external tools your agent should be able to call.</div>
              </div>
              <button className='aep-fn-create-btn' onClick={() => setShowCreateFn(true)}>
                + Create New Function
              </button>
            </div>

            {(detailLoading || toolsLoading) ? (
              <div className='aep-fn-list'>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className='aep-fn-row' style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className='skeleton skeleton-rect' style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div className='skeleton skeleton-text' style={{ width: '45%', height: 11 }} />
                      <div className='skeleton skeleton-text' style={{ width: '70%', height: 10 }} />
                    </div>
                    <div className='skeleton skeleton-rect' style={{ width: 50, height: 18, borderRadius: 4 }} />
                  </div>
                ))}
              </div>
            ) : (
              <div className='aep-fn-list'>
                {allTools.length === 0 ? (
                  <div className='aep-fn-empty-row'>
                    <FiZap size={14} />
                    <span>No functions configured for this agent yet.</span>
                  </div>
                ) : (
                  allTools.map((tool) => (
                    <div
                      key={tool.elevenLabsToolId}
                      className={`aep-fn-row${assignedToolIds.has(tool.elevenLabsToolId) ? ' aep-fn-row--checked' : ''}`}
                    >
                      <input
                        type='checkbox'
                        className='aep-fn-checkbox'
                        checked={assignedToolIds.has(tool.elevenLabsToolId)}
                        onChange={() => toggleToolAssign(tool.elevenLabsToolId)}
                      />
                      <div className='aep-fn-info'>
                        <span className='aep-fn-name'>{tool.name}</span>
                        {tool.description && <span className='aep-fn-desc'>{tool.description}</span>}
                      </div>
                      <div className='aep-fn-badges'>
                        {tool.apiMethod && (
                          <span className='aep-fn-badge aep-fn-badge--method'>
                            {tool.apiMethod.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

          </motion.div>
        )}

        {/* ── Step 5: Transfer Directory ── */}
        {activeStep === 5 && (
          <motion.div
            key='step5'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-fn-header'>
              <div>
                <div className='aep-label aep-label--lg'>Call Transfer Configurations</div>
                <div className='aep-hint'>Define phone numbers and conditions for transferring a call to a human agent.</div>
              </div>
              <button className='aep-fn-create-btn' onClick={addTransferRule}>
                + Add Transfer
              </button>
            </div>

            <div className='aep-tr-list'>
              {transferRules.map((rule, idx) => (
                <div key={rule.id} className='aep-tr-card'>
                  <div className='aep-tr-card__head'>
                    <span className='aep-tr-card__title'>Transfer Rule {idx + 1}</span>
                    {transferRules.length > 1 && (
                      <button
                        className='aep-tr-card__del'
                        onClick={() => removeTransferRule(rule.id)}
                        title='Remove this rule'
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>

                  <div className='aep-field'>
                    <label className='aep-label'>
                      Transfer Phone Number <span className='aep-tr-badge'>E.164</span>
                    </label>
                    <input
                      className='aep-input'
                      value={rule.phone}
                      onChange={(e) => updateTransferRule(rule.id, 'phone', e.target.value)}
                      placeholder='+15136357964'
                    />
                  </div>

                  <div className='aep-field'>
                    <label className='aep-label mt-2'>Condition (Instructions)</label>
                    <textarea
                      className='aep-textarea'
                      rows={3}
                      value={rule.condition}
                      onChange={(e) => updateTransferRule(rule.id, 'condition', e.target.value)}
                      placeholder='Commercial lines are redirected here.'
                    />
                  </div>

                  <div className='aep-field aep-field--last'>
                    <label className='aep-label mt-2'>Transfer Type</label>
                    <select
                      className='aep-select'
                      value={rule.type}
                      onChange={(e) => updateTransferRule(rule.id, 'type', e.target.value)}
                    >
                      <option value='conference'>Conference</option>
                      <option value='blind'>Blind</option>
                      <option value='attended'>Attended</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Step 6: Call End Action ── */}
        {activeStep === 6 && (
          <motion.div
            key='step6'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-cea-header'>
              <div className='aep-label aep-label--lg'>Call End Action (Third-Party)</div>
              <div className='aep-hint'>
                After each call ends, Momentum Voice can send the call details to a third-party API.
                The payload is built by AI from the call transcript and dynamic variables using the template below.
                Leave the URL empty to disable.
              </div>
            </div>

            <div className='aep-field'>
              <label className='aep-label'>Request URL</label>
              <input
                className='aep-input'
                value={ceaUrl}
                onChange={(e) => setCeaUrl(e.target.value)}
                placeholder='https://api.example.com/call-end-webhook'
              />
            </div>

            <div className='aep-cea-card'>
              <div className='aep-cea-row2'>
                <div className='aep-field'>
                  <label className='aep-label'>Method</label>
                  <select className='aep-select' value={ceaMethod} onChange={(e) => setCeaMethod(e.target.value)}>
                    <option value='POST'>POST</option>
                    <option value='PUT'>PUT</option>
                    <option value='PATCH'>PATCH</option>
                  </select>
                </div>
                <div className='aep-field'>
                  <label className='aep-label'>Content-Type</label>
                  <select className='aep-select' value={ceaContentType} onChange={(e) => setCeaContentType(e.target.value)}>
                    <option value='application/json'>application/json</option>
                    <option value='application/x-www-form-urlencoded'>application/x-www-form-urlencoded</option>
                    <option value='text/plain'>text/plain</option>
                  </select>
                </div>
              </div>

              <div className='aep-cea-row2'>
                <div className='aep-field'>
                  <label className='aep-label'>Authorization Type</label>
                  <select className='aep-select' value={ceaAuthType} onChange={(e) => setCeaAuthType(e.target.value)}>
                    <option value='None'>None</option>
                    <option value='Basic'>Basic</option>
                    <option value='Bearer'>Bearer</option>
                    <option value='Custom'>Custom</option>
                  </select>
                </div>
                <div className='aep-field'>
                  <label className='aep-label'>
                    Authorization Value
                    {(ceaHasStoredAuth || ceaAuthValue) && !ceaAuthFocused && (
                      <span className='aep-cea-saved-badge'>saved</span>
                    )}
                  </label>
                  <input
                    className='aep-input'
                    type='text'
                    value={ceaAuthFocused ? ceaAuthValue : (ceaHasStoredAuth || ceaAuthValue) ? '•••••' : ''}
                    onChange={(e) => { setCeaAuthValue(e.target.value); setCeaAuthDirty(true); }}
                    onFocus={() => setCeaAuthFocused(true)}
                    onBlur={() => setCeaAuthFocused(false)}
                    autoComplete='off'
                    placeholder='Enter authorization value'
                  />
                  <span className='aep-hint'>
                    {(ceaHasStoredAuth || ceaAuthValue) && !ceaAuthFocused
                      ? 'A value is saved. Focus the field to replace it.'
                      : 'Sent as the Authorization header (e.g. "Basic …" or "Bearer …").'}
                  </span>
                </div>
              </div>
            </div>

            <div className='aep-field'>
              <label className='aep-label'>
                Extra Headers <span className='aep-hint aep-hint--inline'>(JSON, optional)</span>
              </label>
              <textarea
                className='aep-textarea aep-textarea--mono'
                rows={3}
                value={ceaHeaders}
                onChange={(e) => setCeaHeaders(e.target.value)}
                placeholder={'{ "X-Custom-Header": "value" }'}
              />
              <span className='aep-hint'>
                Authorization and Content-Type are set from the fields above — no need to repeat them here.
              </span>
            </div>

            <div className='aep-field'>
              <label className='aep-label'>Payload Template &amp; Instructions</label>
              <textarea
                className='aep-textarea aep-textarea--mono'
                rows={8}
                value={ceaPayload}
                onChange={(e) => setCeaPayload(e.target.value)}
                placeholder={`Describe the JSON body to send. Use {{placeholder}} for values from the call,\n<...> for values the AI should derive, and add instructions/allowed values in plain text.\n\nExample:\n{\n  "id": "{{policy_number}}",\n  "status": "<pick one of the allowed values below>",\n  "timestamp": "<ISO-8601 UTC timestamp of call end>",\n  "durationSeconds": <integer>\n}`}
              />
              <span className='aep-hint'>
                Define any JSON shape you need. Use <code className='aep-cea-code'>{'{{placeholder}}'}</code> for values from the call and <code className='aep-cea-code'>{'<...>'}</code> for values the AI should derive, and add any allowed-value instructions in plain text. The AI fills it from the call's dynamic variables, outcome signals, and transcript; values it can't determine are left empty.
              </span>
            </div>
          </motion.div>
        )}

        {/* Footer nav */}
        <div className='aep-footer'>
          <button
            className='aep-footer-btn aep-footer-btn--prev'
            onClick={() => activeStep === 1 ? onBack() : setActiveStep((s) => s - 1)}
            disabled={saving}
          >
            ← Back
          </button>

          <div className='aep-footer-right'>
            {activeStep < STEPS.length ? (
              <button
                className='aep-footer-btn aep-footer-btn--next'
                onClick={() => setActiveStep((s) => Math.min(STEPS.length, s + 1))}
                disabled={saving}
              >
                Next →
              </button>
            ) : (
              <button
                className='aep-footer-btn aep-footer-btn--save'
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? <><FiRefreshCw size={13} className='pn-spin' /> Saving…</>
                  : <><FiCheck size={13} /> Save Changes</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Create Function Drawer ── */}
      {showCreateFn && (
        <>
          <div className='aep-cf-backdrop' onClick={() => setShowCreateFn(false)} />

          <motion.div
            className='aep-cf-drawer'
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
          >
            <div className='aep-cf-drawer__head'>
              <div>
                <div className='aep-cf-drawer__title'>Create Function</div>
                <div className='aep-cf-drawer__sub'>Create your own custom function that your voice agent can use</div>
              </div>
              <button className='aep-cf-drawer__close' onClick={() => setShowCreateFn(false)}>
                <FiX size={18} />
              </button>
            </div>

            <div className='aep-cf-drawer__body'>
              <div className='aep-cf-card'>
                <div className='aep-cf-card__title'>Configuration</div>
                <div className='aep-cf-card__sub'>Describe to your agents how and when to use the function.</div>

                <div className='aep-field'>
                  <label className='aep-label'>Name</label>
                  <input
                    className='aep-input'
                    value={cfName}
                    onChange={(e) => setCfName(e.target.value)}
                    placeholder='Name of Function'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>Method</label>
                  <select className='aep-select' value={cfMethod} onChange={(e) => setCfMethod(e.target.value)}>
                    <option value='GET'>GET</option>
                    <option value='POST'>POST</option>
                    <option value='PUT'>PUT</option>
                    <option value='PATCH'>PATCH</option>
                    <option value='DELETE'>DELETE</option>
                  </select>
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>URL</label>
                  <input
                    className='aep-input'
                    value={cfUrl}
                    onChange={(e) => setCfUrl(e.target.value)}
                    placeholder='https://api.example.com/endpoint'
                  />
                </div>

                <div className='aep-field'>
                  <label className='aep-label'>Description</label>
                  <textarea
                    className='aep-textarea'
                    rows={4}
                    value={cfDesc}
                    onChange={(e) => setCfDesc(e.target.value)}
                    placeholder='Description'
                  />
                </div>
              </div>

              <div className='aep-cf-params-head'>
                <div>
                  <div className='aep-cf-params-title'>Parameters</div>
                  <div className='aep-cf-params-sub'>
                    Define parameters that will be collected by your agents and sent as the query of the request.
                  </div>
                </div>
                <button className='aep-cf-add-param-btn' onClick={addCfParam}>
                  <FiPlus size={13} /> Add Parameter
                </button>
              </div>

              {cfParams.map((param) => (
                <div key={param.id} className='aep-cf-card aep-cf-param-card'>
                  <div className='aep-cf-param-card__del-row'>
                    <button className='aep-cf-param-del' onClick={() => removeCfParam(param.id)} title='Remove parameter'>
                      <FiTrash2 size={14} />
                    </button>
                  </div>

                  <div className='aep-field'>
                    <label className='aep-label'>Data Type</label>
                    <select
                      className='aep-select'
                      value={param.dataType}
                      onChange={(e) => updateCfParam(param.id, 'dataType', e.target.value)}
                    >
                      <option value='String'>String</option>
                      <option value='Number'>Number</option>
                      <option value='Boolean'>Boolean</option>
                      <option value='Array'>Array</option>
                      <option value='Object'>Object</option>
                    </select>
                  </div>

                  <div className='aep-field'>
                    <label className='aep-label'>Identifier</label>
                    <input
                      className='aep-input'
                      value={param.identifier}
                      onChange={(e) => updateCfParam(param.id, 'identifier', e.target.value)}
                      placeholder='Identifier'
                    />
                  </div>

                  <div className='aep-field'>
                    <label className='aep-label'>Description</label>
                    <div className='aep-hint'>
                      This field will be passed to the LLM and should describe how to extract data from the transcript.
                    </div>
                    <textarea
                      className='aep-textarea'
                      rows={3}
                      value={param.description}
                      onChange={(e) => updateCfParam(param.id, 'description', e.target.value)}
                      placeholder='Description'
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className='aep-cf-drawer__footer'>
              <button className='aep-footer-btn aep-footer-btn--save' onClick={handleCreateFunction} disabled={creatingFn}>
                {creatingFn
                  ? <><FiRefreshCw size={13} className='pn-spin' /> Creating…</>
                  : <><FiCheck size={14} /> Create Function</>}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default AgentEditPanel;
